import {
  AlertCircle,
  ArrowRight,
  BriefcaseBusiness,
  CheckCircle2,
  ClipboardList,
  FileSearch,
  FileText,
  FolderCheck,
  MailCheck,
  MessageSquareText,
  Route,
  Scale,
  Search,
  ShieldAlert,
} from "lucide-react";
import { useMemo, useState } from "react";

import Button from "./Button.jsx";
import LinkButton from "./LinkButton.jsx";
import SectionHeader from "./SectionHeader.jsx";
import { issueCategories, outcomes } from "./categories.js";

function getInitialIssue() {
  const params = new URLSearchParams(window.location.search);
  const requestedIssue = params.get("issue");

  const issueExists = issueCategories.some(
    (category) => category.id === requestedIssue
  );

  return issueExists ? requestedIssue : issueCategories[0]?.id || "";
}

function getCategoryLabel(categoryId) {
  return (
    issueCategories.find((category) => category.id === categoryId)?.label || ""
  );
}

function filterIssueCategories(searchValue) {
  const value = searchValue.trim().toLowerCase();

  if (!value) {
    return issueCategories.slice(0, 12);
  }

  return issueCategories
    .filter((category) =>
      `${category.label} ${category.description}`.toLowerCase().includes(value)
    )
    .slice(0, 12);
}

function textIncludes(text, words) {
  const value = text.toLowerCase();
  return words.some((word) => value.includes(word));
}

function getIssueProfile(category) {
  const text = `${category.id} ${category.label} ${category.description}`;

  const defaultProfile = {
    industryAdvice: [
      "Identify the business, product, service, transaction date, amount paid, and the specific problem.",
      "Review the business policy, contract terms, receipt, advertisement, warranty, or written promise connected to the issue.",
      "Keep communication factual and organized by date.",
    ],
    complaintSpecificAdvice: [
      "Explain what happened, what you expected, how the business responded, and what remains unresolved.",
      "State the exact outcome requested, such as refund, repair, replacement, cancellation, correction, or written explanation.",
      "Separate facts from opinions and avoid unnecessary personal information.",
    ],
    records: [
      "Receipts, contracts, invoices, bills, statements, advertisements, written policies, and proof of payment",
      "Emails, text messages, chat logs, letters, screenshots, call notes, and confirmation numbers",
      "Photos, videos, delivery records, repair records, inspection documents, or other proof related to the issue",
      "A written timeline with dates, names, amounts, promises made, actions taken, and responses received",
    ],
    demand: [
      "A 30-day demand letter may be relevant if the dispute involves an unfair or deceptive business practice under Massachusetts consumer protection law.",
      "The letter should describe the complaint, the harm or loss, and the specific relief requested.",
      "Keep a copy of the letter and supporting documents. Do not send originals.",
    ],
    escalation: [
      "If the business does not respond or the matter is outside local consumer assistance scope, review whether another local, state, or federal agency is more appropriate.",
      "If the issue involves court papers, legal deadlines, fraud, identity theft, or essential services, seek appropriate help promptly.",
    ],
    referral:
      "Depending on the issue, another local, state, or federal agency may be better suited to review the matter.",
  };

  if (
    textIncludes(text, [
      "auto",
      "car",
      "vehicle",
      "dealer",
      "repair",
      "lemon",
      "repossession",
      "motorcycle",
      "rv",
    ])
  ) {
    return {
      industryAdvice: [
        "Identify whether the issue involves auto repair, vehicle purchase, financing, warranty, repossession, insurance, title, or registration.",
        "For repairs, compare the written estimate, authorized work, final invoice, and warranty terms.",
        "For sales or financing, review the purchase contract, financing documents, add-ons, warranty, and dealer representations.",
      ],
      complaintSpecificAdvice: [
        "State the vehicle year, make, model, VIN, mileage, purchase or repair date, and amount paid.",
        "Explain whether the problem is defective work, unauthorized work, misrepresentation, financing terms, warranty denial, or failure to deliver title or registration.",
        "Describe the remedy requested: refund, repair, corrected invoice, cancellation, title correction, warranty service, or written explanation.",
      ],
      records: [
        "Purchase contract, financing agreement, repair order, written estimate, invoice, warranty, and service agreement",
        "Vehicle year, make, model, VIN, mileage, license plate, and dealer or repair shop information",
        "Photos, inspection reports, diagnostic reports, towing records, text messages, emails, and call notes",
        "Timeline of purchase, repair, pickup, return visits, payment dates, and when the problem was first reported",
      ],
      demand: [
        "A demand letter should identify the vehicle or repair order and explain exactly what was promised versus what happened.",
        "Attach copies of estimates, invoices, contracts, photos, warranties, and messages.",
        "Request a specific remedy such as repair, refund, corrected bill, warranty service, or cancellation.",
      ],
      escalation: [
        "Auto issues may require different referral paths depending on whether the issue involves repair work, sales, financing, warranty, repossession, or insurance.",
        "If the issue involves immediate repossession, court papers, or financing default, seek legal or agency guidance promptly.",
      ],
      referral:
        "Auto disputes may involve dealer licensing, repair regulations, insurance, financing, warranty providers, or court options depending on the facts.",
    };
  }

  if (
    textIncludes(text, [
      "home improvement",
      "contractor",
      "roof",
      "hvac",
      "plumbing",
      "electrical",
      "flooring",
      "painting",
      "permit",
      "unfinished",
      "workmanship",
      "deposit",
      "solar",
    ])
  ) {
    return {
      industryAdvice: [
        "Confirm whether the business is a contractor, subcontractor, salesperson, installer, or financing company.",
        "Compare the signed contract, change orders, payments, project schedule, and work actually completed.",
        "Check whether permits, inspections, registrations, warranties, or manufacturer documents are relevant.",
      ],
      complaintSpecificAdvice: [
        "Describe whether the issue is unfinished work, poor workmanship, delay, deposit dispute, overbilling, permit problem, or warranty refusal.",
        "List each disputed item separately and connect it to the contract, invoice, photo, or message that supports it.",
        "State whether you want completion, repair, refund, accounting, warranty service, or cancellation.",
      ],
      records: [
        "Signed contract, proposal, change orders, invoices, receipts, proof of payment, and warranty documents",
        "Photos or videos before, during, and after the work",
        "Permit, inspection, license, registration, subcontractor, and insurance information if available",
        "Messages about delays, incomplete work, poor workmanship, deposits, cancellations, or requested corrections",
      ],
      demand: [
        "A demand letter should identify the project address, contract date, work promised, amount paid, and disputed work.",
        "Include photos, invoices, inspection records, and written communication.",
        "Request completion, repair, refund, itemized accounting, warranty service, or release from contract.",
      ],
      escalation: [
        "Home improvement issues may involve contractor registration, local inspectional services, licensing agencies, mediation, or court options.",
        "If unsafe work, code issues, or permit problems are involved, contact the appropriate inspectional or licensing resource promptly.",
      ],
      referral:
        "Contractor disputes may require local inspectional services, contractor registration review, licensing agencies, mediation, or legal resources.",
    };
  }

  if (
    textIncludes(text, [
      "debt",
      "collection",
      "credit report",
      "credit repair",
      "settlement",
      "garnishment",
      "loan",
      "payday",
      "student",
      "mortgage",
      "foreclosure",
    ])
  ) {
    return {
      industryAdvice: [
        "Identify whether the issue involves debt collection, credit reporting, loan servicing, mortgage servicing, settlement, or court collection.",
        "Do not ignore court papers, collection notices, or deadlines.",
        "If you dispute the debt, communicate carefully and keep copies of every written dispute or validation request.",
      ],
      complaintSpecificAdvice: [
        "State whether you recognize the debt, dispute the debt, believe the amount is wrong, or believe the collection conduct is improper.",
        "Identify the collector, original creditor, account number, amount claimed, and dates of contact.",
        "Describe the specific problem: harassment, incorrect credit reporting, failure to validate, wrong person, already paid, identity theft, or lawsuit.",
      ],
      records: [
        "Collection letters, account statements, court papers, credit reports, and notices",
        "Call logs with dates, times, numbers, and names of collectors or representatives",
        "Dispute letters, validation requests, payment records, settlement offers, and confirmation numbers",
        "Identity theft reports, fraud reports, or records showing the debt is incorrect if applicable",
      ],
      demand: [
        "A demand letter or dispute letter should clearly state what is disputed and what correction or response is requested.",
        "Avoid admitting responsibility for a debt you dispute without understanding the consequences.",
        "Keep proof of mailing, copies of all documents, and any response received.",
      ],
      escalation: [
        "If you received court papers, wage garnishment notices, foreclosure notices, or legal deadlines, seek legal help promptly.",
        "Credit reporting issues may also require disputes with credit bureaus or the furnisher of the information.",
      ],
      referral:
        "Debt and credit matters may involve consumer agencies, credit bureaus, legal aid, court deadlines, or financial regulators.",
    };
  }

  if (
    textIncludes(text, [
      "bank",
      "credit card",
      "wire",
      "atm",
      "mobile payment",
      "prepaid",
      "overdraft",
      "money transfer",
      "unauthorized charge",
      "billing error",
    ])
  ) {
    return {
      industryAdvice: [
        "Use the financial institution’s official written dispute process whenever possible.",
        "Track deadlines for billing errors, unauthorized transactions, fraud disputes, and provisional credits.",
        "Save confirmation numbers, secure messages, letters, and final investigation results.",
      ],
      complaintSpecificAdvice: [
        "Identify the transaction date, amount, merchant, account type, and dispute reference number.",
        "Explain whether the issue is unauthorized, incorrect, duplicated, not delivered, not credited, or improperly denied.",
        "State whether you want reversal, refund, fee correction, investigation, account correction, or written explanation.",
      ],
      records: [
        "Statements, transaction history, receipts, dispute forms, confirmation numbers, and account notices",
        "Messages, letters, secure account communications, fraud reports, and investigation results",
        "Screenshots showing the transaction, merchant, date, amount, and dispute status",
        "Timeline of the transaction, dispute date, bank response, and any provisional credit or denial",
      ],
      demand: [
        "A demand letter should identify the transaction and explain why the financial institution or merchant response is disputed.",
        "Include statements, receipts, dispute confirmations, and final decision letters.",
        "Request correction, refund, investigation, reversal, written explanation, or account update.",
      ],
      escalation: [
        "Banking and payment disputes may need referral to a financial regulator or federal consumer agency.",
        "If fraud is involved, secure accounts, update passwords, and consider additional identity protection steps.",
      ],
      referral:
        "Banking, credit card, money transfer, and payment app disputes may require financial regulators or federal consumer agencies.",
    };
  }

  return defaultProfile;
}

function getBusinessContactGuidance(contacted) {
  if (contacted === "Yes") {
    return "You have already contacted the business. Organize proof of that contact, including dates, names, messages, confirmation numbers, and the response you received.";
  }

  if (contacted === "No") {
    return "When safe and appropriate, contact the business in writing before escalating. A written request creates a record and may help resolve the dispute without further action.";
  }

  return "If you are not sure whether contact was made, review emails, texts, account messages, receipts, and call logs to confirm what has already been attempted.";
}

function getOutcomeGuidance(outcome) {
  if (!outcome) {
    return "Select the outcome you are requesting so the complaint can clearly state what would resolve the matter.";
  }

  return `Because you selected "${outcome}", explain why that outcome is appropriate and include records that support the request.`;
}

function buildDemandLetterChecklist(category, outcome) {
  return [
    "Use your current mailing address, daytime phone number, email address, and the date of the letter.",
    "List the business name, mailing address, account number, order number, contract number, or other identifying information.",
    "State that you are making a written request for relief and describe the issue clearly in date order.",
    "Explain the harm, loss, disputed amount, or practical problem caused by the business conduct.",
    `State the requested relief clearly${outcome ? `, such as ${outcome.toLowerCase()}` : ""}.`,
    "Attach copies of supporting records, such as receipts, contracts, bills, repair orders, screenshots, advertisements, and messages. Do not send originals.",
    "Send the letter by a method that creates proof of mailing and delivery, such as certified mail, and keep a complete copy.",
    "Calendar 30 days from the date the business receives the letter before considering court options based on a Chapter 93A demand letter.",
    `Tailor the letter to the issue category: ${category.label}.`,
  ];
}

function buildProfessionalPlan({ selected, contacted, outcome, notes }) {
  const profile = getIssueProfile(selected);

  const immediateActions = [
    "Write a short summary of the dispute in plain language.",
    "Create a timeline with purchase, service, cancellation, repair, billing, payment, or contact dates.",
    "Identify the business name, address, website, phone number, email address, and any account, order, invoice, or contract number.",
    "State the specific outcome being requested, such as refund, repair, replacement, cancellation, correction, or written explanation.",
  ];

  const complaintQuality = [
    "Use facts, dates, amounts, and documents rather than assumptions or conclusions.",
    "Explain what was promised, what happened, how the business responded, and what remains unresolved.",
    "Attach copies of supporting records. Do not send originals.",
    "Avoid including Social Security numbers, passwords, full bank account numbers, or unnecessary sensitive information.",
  ];

  const notesGuidance =
    notes.trim().length >= 100
      ? "Your description has a useful amount of detail. Review it for key dates, dollar amounts, business names, and the exact outcome requested."
      : "Your description may need more detail. Add key dates, what happened, who you contacted, what the business said, and what outcome you are requesting.";

  return {
    immediateActions,
    industryAdvice: profile.industryAdvice,
    complaintSpecificAdvice: profile.complaintSpecificAdvice,
    records: profile.records,
    communication: profile.demand,
    complaintQuality,
    demandLetterChecklist: buildDemandLetterChecklist(selected, outcome),
    escalation: profile.escalation,
    referralConsiderations: [profile.referral],
    notesGuidance,
    contactGuidance: getBusinessContactGuidance(contacted),
    outcomeGuidance: getOutcomeGuidance(outcome),
  };
}

function RecommendationSection({ icon: Icon, title, children }) {
  return (
    <section className="rounded-md border border-civic-line bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-5 w-5 text-civic-blue" aria-hidden="true" />
        <h3 className="font-bold text-civic-navy">{title}</h3>
      </div>
      {children}
    </section>
  );
}

export default function Start({ navigate }) {
  const initialIssue = getInitialIssue();

  const [issueId, setIssueId] = useState(initialIssue);
  const [issueSearch, setIssueSearch] = useState(() =>
    getCategoryLabel(initialIssue)
  );
  const [showIssueOptions, setShowIssueOptions] = useState(false);
  const [contacted, setContacted] = useState("");
  const [outcome, setOutcome] = useState("");
  const [notes, setNotes] = useState("");
  const [showGuidance, setShowGuidance] = useState(false);

  const selected = useMemo(() => {
    return (
      issueCategories.find((category) => category.id === issueId) ||
      issueCategories.find((category) => category.id === "other-consumer-issue") ||
      issueCategories[0]
    );
  }, [issueId]);

  const visibleIssueOptions = useMemo(
    () => filterIssueCategories(issueSearch),
    [issueSearch]
  );

  const recommendationPlan = useMemo(() => {
    if (!selected) {
      return null;
    }

    return buildProfessionalPlan({
      selected,
      contacted,
      outcome,
      notes,
    });
  }, [selected, contacted, outcome, notes]);

  const canContinue = Boolean(issueId && contacted && outcome);

  const selectIssue = (category) => {
    setIssueId(category.id);
    setIssueSearch(category.label);
    setShowIssueOptions(false);
    setShowGuidance(false);
  };

  if (!selected) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold text-civic-navy">
          No issue categories are available
        </h1>
        <p className="mt-3 text-slate-600">
          Please check the categories configuration file.
        </p>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <SectionHeader
        eyebrow="Guided Review"
        title="Start with a structured consumer issue review"
      >
        Answer a few questions to receive organized next steps, document
        suggestions, demand letter preparation guidance, and referral
        considerations before preparing a complaint.
      </SectionHeader>

      <div className="mt-8 grid gap-6 lg:grid-cols-[.9fr_1.1fr]">
        <div className="rounded-md border border-civic-line bg-white p-5 shadow-sm">
          <div className="mb-5 flex items-center gap-3">
            <ClipboardList
              className="h-6 w-6 text-civic-blue"
              aria-hidden="true"
            />
            <h2 className="text-lg font-bold text-civic-navy">
              Consumer Issue Details
            </h2>
          </div>

          <div className="space-y-5">
            <div>
              <label className="label" htmlFor="issueSearch">
                Issue type
              </label>

              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-slate-400"
                  aria-hidden="true"
                />

                <input
                  id="issueSearch"
                  type="search"
                  className="field pl-10"
                  value={issueSearch}
                  onFocus={() => setShowIssueOptions(true)}
                  onChange={(event) => {
                    setIssueSearch(event.target.value);
                    setShowIssueOptions(true);
                    setShowGuidance(false);
                  }}
                  placeholder="Search refund, auto repair, debt, housing, fraud..."
                  autoComplete="off"
                />

                {showIssueOptions && (
                  <div className="absolute z-30 mt-2 max-h-72 w-full overflow-auto rounded-md border border-civic-line bg-white shadow-panel">
                    {visibleIssueOptions.length > 0 ? (
                      visibleIssueOptions.map((category) => (
                        <button
                          key={category.id}
                          type="button"
                          className="block w-full border-b border-civic-line px-4 py-3 text-left text-sm last:border-b-0 hover:bg-civic-sky"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            selectIssue(category);
                          }}
                        >
                          <span className="block font-bold text-civic-navy">
                            {category.label}
                          </span>

                          <span className="mt-1 block text-xs leading-5 text-slate-500">
                            {category.description}
                          </span>
                        </button>
                      ))
                    ) : (
                      <button
                        type="button"
                        className="block w-full px-4 py-3 text-left text-sm hover:bg-civic-sky"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          selectIssue({
                            id: "other-consumer-issue",
                            label: issueSearch.trim() || "Other Consumer Issue",
                          });
                        }}
                      >
                        <span className="block font-bold text-civic-navy">
                          Use "{issueSearch.trim() || "Other Consumer Issue"}"
                        </span>

                        <span className="mt-1 block text-xs leading-5 text-slate-500">
                          Select this if the issue does not match the listed
                          categories.
                        </span>
                      </button>
                    )}
                  </div>
                )}
              </div>

              <p className="help-text">
                Start typing to search the full issue list without scrolling
                through a long dropdown.
              </p>
            </div>

            <fieldset>
              <legend className="label">
                Have you contacted the business?
              </legend>

              <div className="grid gap-2 sm:grid-cols-3">
                {["Yes", "No", "Not sure"].map((value) => (
                  <label
                    key={value}
                    className="flex cursor-pointer items-center gap-2 rounded-md border border-civic-line p-3 text-sm"
                  >
                    <input
                      type="radio"
                      name="contacted"
                      value={value}
                      checked={contacted === value}
                      onChange={(event) => {
                        setContacted(event.target.value);
                        setShowGuidance(false);
                      }}
                    />
                    {value}
                  </label>
                ))}
              </div>
            </fieldset>

            <div>
              <label className="label" htmlFor="outcome">
                Desired outcome
              </label>

              <select
                id="outcome"
                className="field"
                value={outcome}
                onChange={(event) => {
                  setOutcome(event.target.value);
                  setShowGuidance(false);
                }}
              >
                <option value="">Select outcome</option>
                {outcomes.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label" htmlFor="notes">
                Briefly describe what happened
              </label>

              <textarea
                id="notes"
                className="field min-h-32"
                value={notes}
                onChange={(event) => {
                  setNotes(event.target.value);
                  setShowGuidance(false);
                }}
                placeholder="Include dates, what happened, who you contacted, what response you received, and what outcome you want."
              />

              <p className="help-text">
                Avoid entering Social Security numbers, bank account numbers,
                credit card numbers, or passwords.
              </p>
            </div>

            <Button
              disabled={!canContinue}
              onClick={() => setShowGuidance(true)}
              className="w-full sm:w-auto"
            >
              Show professional next steps
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>

        <div className="space-y-5">
          {!showGuidance || !recommendationPlan ? (
            <div className="rounded-md border border-civic-line bg-white p-6 shadow-sm">
              <div className="rounded-md bg-slate-50 p-6">
                <h2 className="text-xl font-bold text-civic-navy">
                  {selected.label}
                </h2>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  {selected.description}
                </p>

                <div className="mt-6 text-sm font-bold text-civic-navy">
                  This review will help organize:
                </div>

                <ul className="mt-3 space-y-2 text-sm text-slate-600">
                  {(selected.questions || []).map((question) => (
                    <li key={question} className="flex gap-2">
                      <CheckCircle2
                        className="mt-0.5 h-4 w-4 shrink-0 text-civic-blue"
                        aria-hidden="true"
                      />
                      {question}
                    </li>
                  ))}
                </ul>

                <div className="mt-5 rounded-md border border-civic-line bg-white p-4 text-sm leading-6 text-slate-600">
                  Complete the fields on the left to receive detailed next
                  steps, demand letter preparation guidance, document
                  suggestions, and referral considerations.
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="rounded-md border border-civic-line bg-white p-5 shadow-sm">
                <div className="rounded-md bg-civic-sky p-4">
                  <div className="text-sm font-bold text-civic-blue">
                    Recommended Next Steps
                  </div>

                  <h2 className="mt-1 text-xl font-bold text-civic-navy">
                    {selected.label}
                  </h2>

                  <p className="mt-2 text-sm leading-6 text-slate-700">
                    These recommendations help organize the issue, prepare a
                    written complaint, evaluate whether a 30-day demand letter
                    may be appropriate, and identify referral considerations.
                  </p>
                </div>

                <div className="mt-4 grid gap-3 text-sm leading-6 text-slate-700">
                  <div className="rounded-md border border-civic-line p-3">
                    <strong className="text-civic-navy">
                      Business contact:
                    </strong>{" "}
                    {recommendationPlan.contactGuidance}
                  </div>

                  <div className="rounded-md border border-civic-line p-3">
                    <strong className="text-civic-navy">
                      Requested outcome:
                    </strong>{" "}
                    {recommendationPlan.outcomeGuidance}
                  </div>

                  <div className="rounded-md border border-civic-line p-3">
                    <strong className="text-civic-navy">
                      Description quality:
                    </strong>{" "}
                    {recommendationPlan.notesGuidance}
                  </div>
                </div>
              </div>

              <RecommendationSection icon={Route} title="Immediate next steps">
                <ol className="space-y-3">
                  {recommendationPlan.immediateActions.map((step, index) => (
                    <li key={step} className="flex gap-3 text-sm leading-6">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-civic-blue text-sm font-bold text-white">
                        {index + 1}
                      </span>
                      <span className="text-slate-700">{step}</span>
                    </li>
                  ))}
                </ol>
              </RecommendationSection>

              <RecommendationSection
                icon={BriefcaseBusiness}
                title="Industry-specific guidance"
              >
                <ul className="space-y-2 text-sm leading-6 text-slate-700">
                  {recommendationPlan.industryAdvice.map((item) => (
                    <li key={item} className="flex gap-2">
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-civic-blue" />
                      {item}
                    </li>
                  ))}
                </ul>
              </RecommendationSection>

              <RecommendationSection
                icon={FileSearch}
                title="Complaint-specific advice"
              >
                <ul className="space-y-2 text-sm leading-6 text-slate-700">
                  {recommendationPlan.complaintSpecificAdvice.map((item) => (
                    <li key={item} className="flex gap-2">
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-civic-blue" />
                      {item}
                    </li>
                  ))}
                </ul>
              </RecommendationSection>

              <RecommendationSection icon={FolderCheck} title="Records to gather">
                <ul className="space-y-2 text-sm leading-6 text-slate-700">
                  {recommendationPlan.records.map((record) => (
                    <li key={record} className="flex gap-2">
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-civic-blue" />
                      {record}
                    </li>
                  ))}
                </ul>
              </RecommendationSection>

              <RecommendationSection
                icon={MailCheck}
                title="30-day demand letter preparation"
              >
                <div className="mb-3 rounded-md bg-slate-50 p-3 text-sm leading-6 text-slate-700">
                  Massachusetts consumer protection guidance explains that if a
                  consumer is unable to resolve a complaint informally, a
                  written 30-day demand letter may be required before filing a
                  Chapter 93A consumer claim in court. This portal does not
                  provide legal advice, but it can help organize the information
                  commonly included in that letter.
                </div>

                <ul className="space-y-2 text-sm leading-6 text-slate-700">
                  {recommendationPlan.demandLetterChecklist.map((item) => (
                    <li key={item} className="flex gap-2">
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-civic-blue" />
                      {item}
                    </li>
                  ))}
                </ul>
              </RecommendationSection>

              <RecommendationSection
                icon={MessageSquareText}
                title="Category-specific letter details"
              >
                <ul className="space-y-2 text-sm leading-6 text-slate-700">
                  {recommendationPlan.communication.map((item) => (
                    <li key={item} className="flex gap-2">
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-civic-blue" />
                      {item}
                    </li>
                  ))}
                </ul>
              </RecommendationSection>

              <RecommendationSection
                icon={FileText}
                title="What makes the complaint stronger"
              >
                <ul className="space-y-2 text-sm leading-6 text-slate-700">
                  {recommendationPlan.complaintQuality.map((item) => (
                    <li key={item} className="flex gap-2">
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-civic-blue" />
                      {item}
                    </li>
                  ))}
                </ul>
              </RecommendationSection>

              <RecommendationSection
                icon={ShieldAlert}
                title="When to escalate or seek another resource"
              >
                <ul className="space-y-2 text-sm leading-6 text-slate-700">
                  {recommendationPlan.escalation.map((item) => (
                    <li key={item} className="flex gap-2">
                      <AlertCircle className="mt-1 h-4 w-4 shrink-0 text-civic-blue" />
                      {item}
                    </li>
                  ))}
                </ul>
              </RecommendationSection>

              <RecommendationSection icon={Scale} title="Important note">
                <p className="text-sm leading-6 text-slate-700">
                  Demand letters, court claims, agency complaints, and legal
                  deadlines can have consequences. This guidance is for
                  organizing consumer information and is not legal advice. If
                  you have questions about legal rights, court deadlines, or the
                  specific application of Chapter 93A, consider contacting an
                  attorney, legal aid resource, court service center, or the
                  appropriate consumer protection agency.
                </p>
              </RecommendationSection>

              <div className="rounded-md border border-civic-line bg-white p-5 shadow-sm">
                <h3 className="font-bold text-civic-navy">
                  Ready to continue?
                </h3>

                <p className="mt-2 text-sm leading-6 text-slate-600">
                  If your information is organized, continue to the complaint
                  preparation form. You can also review resource links first if
                  you are unsure which agency or organization may be
                  appropriate.
                </p>

                <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                  <LinkButton href="/complaint" navigate={navigate}>
                    Continue to complaint preparation
                  </LinkButton>

                  <LinkButton
                    href="/resources"
                    navigate={navigate}
                    variant="secondary"
                  >
                    View resources
                  </LinkButton>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
